import { useState, useEffect } from 'react'
import PageHeader from './PageHeader'
import Breadcrumb from './Breadcrumb'
import getImageUrl from './get-image-url'
import { useMediaQuery } from 'react-responsive'
import ReactPlayer from 'react-player/youtube'
import apiFetch from '@wordpress/api-fetch'
import { motion } from 'framer-motion'
import parse from 'html-react-parser'
import clsx from 'clsx'

const fadeInBottom = {
  hidden: {
    opacity: 0,
    y: 80,
  },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      y: {
        duration: 1.3,
        ease: [0.165, 0.84, 0.44, 1],
        delay: i || 0,
      },
      opacity: {
        duration: 0.3,
        delay: i || 0,
      },
    },
  }),
}

const childVariantsBg = {
  hidden: { backgroundColor: '#003475', scaleX: 0, originX: 0, width: '100%', height: '100%' },
  visible: {
    scaleX: [0, 1, 1, 0],
    originX: [0, 0, 1, 1],
    transition: {
      duration: 0.6,
    },
  },
}

const Step = (props) => {
  return (
    <>
      <motion.div
        initial="hidden"
        whileInView="visible"
        className="flex h-full gap-x-[8px] lg:gap-x-[51px]"
        viewport={{ once: true, threshold: 0.35 }}
      >
        <div
          className={clsx('step', {
            last: props.last,
          })}
        >
          <img
            src={props.step}
            className="w-[5.5rem] lg:w-[11rem]"
            alt=""
          />
        </div>
        <div
          className={clsx('flex flex-col lg:flex-row w-full lg:justify-between', {
            'pb-[calc(158vw/7.5)] lg:pb-[155px]': !props.last,
          })}
        >
          <div>
            <motion.div variants={fadeInBottom}>
              <p className="heading text-[#f9d022] bg-[#003475] p-1 w-fit">
                {parse(props.heading)}
              </p>
              <p className="paragrah">{parse(props.paragrah)}</p>
            </motion.div>
          </div>
          <div className="relative">
            <motion.div
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    delay: 0.5,
                  },
                },
              }}
            >
              <img
                src={props.image}
                alt=""
                className="mt-2 lg:mt-0 lg:self-end lg:w-[298px]"
              />
            </motion.div>
            <div className="absolute inset-0 m-auto w-full h-full">
              <motion.div variants={childVariantsBg}></motion.div>
            </div>
          </div>
        </div>
      </motion.div>
      <style jsx>{`
        .step {
          position: relative;
        }
        .step:not(.last)::after {
          content: '';
          position: absolute;
          top: 0;
          left: calc(50% - 2px);
          width: 4px;
          background: #2964b6;
          display: block;
          z-index: -1;
          height: 100%;
        }

        .heading {
          font-size: 1.125rem;
          font-weight: bold;
          line-height: 1.5;
        }
        .paragrah {
          font-size: 1.1rem;
          margin-top: 16px;
        }

        @media (min-width: 1024px) {
          .heading {
            font: normal normal bold 20px Noto Sans JP;
          }
          .paragrah {
            font: normal normal normal 17.5px Noto Sans JP;
            margin-top: 20px;
          }
          .step:not(.last)::after {
            left: calc(50% - 4px);
            width: 8px;
          }
        }
      `}</style>
    </>
  )
}

const FlowFromInquiry = () => {
  const isMD = useMediaQuery({ query: '(min-width: 768px)' })
  const isLG = useMediaQuery({ query: '(min-width: 1024px)' })

  const [youtube, setYoutube] = useState('')

  useEffect(() => {
    apiFetch({
      path: '/wp-json/myroutes/flow',
    }).then((res) => {
      setYoutube(res)
    })
  }, [])

  return (
    <>
      <div className="max-w-maximum mx-auto">
        <PageHeader
          title={'お問い合わせからの流れ'}
          image={getImageUrl('FLOW-FROM-INQUIRY.png')}
        />
        <div className="content-area">
          <Breadcrumb
            current={'お問い合わせからの流れ'}
            site_url={import.meta.env.VITE_SITE_URL}
            items={[]}
          />
          <div className="mt-[calc(50vw/7.5)] lg:mt-[102px]">
            <ReactPlayer
              url={youtube && youtube}
              className="aspect-video"
              width={'100%'}
              height={'100%'}
            />
          </div>
          <div className="step-area mt-[calc(146vw/7.5)] lg:mt-[198px]">
            <Step
              step={getImageUrl('step-1.png')}
              heading={`
              お電話・メールフォームにて${isLG ? '<br>' : ''}お問合わせください
            `}
              paragrah={`
            まずはお電話・お問合わせフォームより${
              isLG ? '<br>' : ''
            }ご連絡ください。折り返しご連絡させていただき${
                isLG ? '<br>' : ''
              }今後の流れをご説明いたします。${
                isLG ? '<br>' : ''
              }スピーディーな対応を心がけておりますので、${
                isLG ? '<br>' : ''
              }最短で翌日にはお見積りを提出します。${
                isLG ? '<br>' : ''
              }ご不明点がありましたら、ご相談ください。`}
              image={getImageUrl('step-1-image.png')}
            />
            <Step
              step={getImageUrl('step-2.png')}
              heading={`
              現場調査・お見積り
            `}
              paragrah={`
              現地調査をさせていただき、お見積りを${
                isLG ? '<br>' : ''
              }作成いたします。可能な限りお客様も${
                isLG ? '<br>' : ''
              }お立合いください。建物の面積・構造・内部状況、${
                isLG ? '<br>' : ''
              }外構など解体の有無、近隣状況など${
                isLG ? '<br>' : ''
              }確認させていただきます。また、疑問点など${
                isLG ? '<br>' : ''
              }ありましたら、ご遠慮なくご質問ください。
            `}
              image={getImageUrl('step-2-image.png')}
            />
            <Step
              step={getImageUrl('step-3.png')}
              heading={`
              ご契約
            `}
              paragrah={`
              提出したお見積りをご確認いただき、${
                isLG ? '<br>' : ''
              }ご納得いただけましたら正式に契約をさせて${
                isLG ? '<br>' : ''
              }いただきます。ご不明点やご要望がありましたら${
                isLG ? '<br>' : ''
              }ご納得が頂けるまでご相談ください。${
                isLG ? '<br>' : ''
              }お打合せさせて頂いたスケジュールで${isLG ? '<br>' : ''}解体工事の着工となります。
            `}
              image={getImageUrl('step-3-image.png')}
            />
            <Step
              step={getImageUrl('step-4.png')}
              heading={'各自治体へ必要書類の提出'}
              paragrah={`
              建設リサイクル法で定められた届出が必要な${
                isLG ? '<br>' : ''
              }場合は各自治体への届け出を代行して行ないます。${
                isLG ? '<br>' : ''
              }横浜市ではすべての建築物、その他の地域では${
                isLG ? '<br>' : ''
              }解体する建物面積が80㎡以上の建築物について${
                isLG ? '<br>' : ''
              }解体工事の際に届出を行うことが${isLG ? '<br>' : ''}義務付けられています。
            `}
              image={getImageUrl('step-4-image.png')}
            />
            <Step
              step={getImageUrl('step-5.png')}
              heading={'近隣へのご挨拶'}
              paragrah={`
              工事着工前に円滑な解体工事が行なえるよう、${
                isLG ? '<br>' : ''
              }横浜総合建設のスタッフが近隣の皆様に${
                isLG ? '<br>' : ''
              }ご挨拶と工事のご説明をさせていただきます。${
                isLG ? '<br>' : ''
              }ご挨拶の際には工事期間・弊社連絡先が${
                isLG ? '<br>' : ''
              }記載された挨拶状と粗品を持参いたしますので${
                isLG ? '<br>' : ''
              }ご安心してお任せください。
            `}
              image={getImageUrl('step-5-image.png')}
            />
            <Step
              step={getImageUrl('step-6.png')}
              heading={'引込配管・配線の撤去の手配'}
              paragrah={`
              解体工事を行なう前に、解体工事の妨げとなる${
                isLG ? '<br>' : ''
              }ガス・電気・電話の引込配管・配線の${
                isLG ? '<br>' : ''
              }撤去の手配を行ないます。手配に関しましては${
                isLG ? '<br>' : ''
              }事前にお客様とお打ち合わせさせていただき、${
                isLG ? '<br>' : ''
              }手配方法などを決めさせていただいております。
            `}
              image={getImageUrl('step-6-image.png')}
            />
            <Step
              step={getImageUrl('step-7.png')}
              heading={'解体工事の着工'}
              paragrah={`
              解体工事を着工いたします。工事期間中は${
                isLG ? '<br>' : ''
              }防災シート・水撒き・重機操作など防音・振動・${
                isLG ? '<br>' : ''
              }防塵対策をしっかりと行ない、近隣の皆様へ${
                isLG ? '<br>' : ''
              }配慮した解体工事を行ないます。${
                isLG ? '<br>' : ''
              }また、現場の作業環境や状況によって${isLG ? '<br>' : ''}万全な安全対策を行ないます。
            `}
              image={getImageUrl('step-7-image.png')}
            />
            <Step
              step={getImageUrl('step-8.png')}
              heading={'マニフェストの管理'}
              paragrah={`
              産業廃棄物の不法投棄を防ぐために、${
                isLG ? '<br>' : ''
              }解体工事現場から出た廃棄物がどのように${
                isLG ? '<br>' : ''
              }処理されたかが記載されたマニフェストを${
                isLG ? '<br>' : ''
              }作成します。解体された産業廃棄物に${
                isLG ? '<br>' : ''
              }つきましては横浜総合建設が責任を持って${
                isLG ? '<br>' : ''
              }廃棄いたしますのでご安心ください。
            `}
              image={getImageUrl('step-8-image.png')}
            />
            <Step
              step={getImageUrl('step-9.png')}
              heading={'お引き渡し'}
              paragrah={`
              整地後、お客様立会いの下、契約通りの状態に${
                isLG ? '<br>' : ''
              }なっているかご確認していただき、${
                isLG ? '<br>' : ''
              }お引き渡しとなります。お引き渡しの際に、${
                isLG ? '<br>' : ''
              }滅失登記を行う際に必要となる書類を${isLG ? '<br>' : ''}提出させていただきます。
            `}
              image={getImageUrl('step-9-image.png')}
              last={true}
            />
          </div>
        </div>
      </div>
      <style jsx>{`
        .step-area {
          margin-left: calc(-30vw / 7.5);
        }
        @media (min-width: 768px) {
          .step-area {
            margin-left: 0;
          }
        }
      `}</style>
    </>
  )
}

export default FlowFromInquiry
